/**
 * D&D 2024 Bastion System Data
 *
 * SOURCE: Dungeon Master's Guide 2024
 * PURPOSE: Complete bastion facilities, orders, and mechanics
 *
 * BASTION RULES SUMMARY:
 * - Unlocks at Level 5
 * - Start with 2 basic facilities (1 Cramped, 1 Roomy)
 * - Gain special facilities at levels 5, 9, 13, 17
 * - Bastion Turn occurs every 7 in-game days
 * - Issue orders: Craft, Empower, Harvest, Maintain, Recruit, Research, Trade
 *
 * Created: October 10, 2025
 */

// [PHASE 6A]: Import systemBus for event-driven architecture
import { systemBus, EVENTS } from '../services/systemBus';

// ============================================
// BASTION ORDER TYPES
// ============================================

export const BASTION_ORDERS = {
  craft: {
    name: 'Craft',
    icon: '🔨',
    color: 'amber',
    description: 'Create items, potions, or equipment using facility resources',
    duration: 7, // days
    requiresFacilityTypes: ['smithy', 'laboratory', 'workshop', 'scriptorium'],
    examples: ['Craft weapons', 'Brew potions', 'Create scrolls']
  },
  empower: {
    name: 'Empower',
    icon: '⚡',
    color: 'purple',
    description: 'Enhance magical facilities or imbue items with power',
    duration: 7,
    requiresFacilityTypes: ['arcane_study', 'sanctum', 'teleportation_circle', 'war_room'],
    examples: ['Enchant items', 'Strengthen wards', 'Empower teleportation']
  },
  harvest: {
    name: 'Harvest',
    icon: '🌾',
    color: 'green',
    description: 'Gather resources, food, or materials from the land',
    duration: 7,
    requiresFacilityTypes: ['garden', 'greenhouse', 'hunting_lodge', 'mine'],
    examples: ['Grow herbs', 'Hunt game', 'Mine ore']
  },
  maintain: {
    name: 'Maintain',
    icon: '🔧',
    color: 'gray',
    description: 'Keep facilities in working order (automatic if not issued other orders)',
    duration: 0, // Automatic
    requiresFacilityTypes: [], // Available for all
    examples: ['Repair damage', 'Clean facilities', 'Routine upkeep']
  },
  recruit: {
    name: 'Recruit',
    icon: '👥',
    color: 'blue',
    description: 'Hire defenders, specialists, or gather followers',
    duration: 7,
    requiresFacilityTypes: ['barracks', 'pub', 'guildhall', 'stable'],
    examples: ['Recruit soldiers', 'Hire specialists', 'Gather followers']
  },
  research: {
    name: 'Research',
    icon: '📚',
    color: 'indigo',
    description: 'Study lore, identify items, gather information, or learn secrets',
    duration: 7,
    requiresFacilityTypes: ['arcane_study', 'library', 'observatory', 'archive', 'meditation_chamber'],
    examples: ['Identify magic items', 'Research spells', 'Gather rumors']
  },
  trade: {
    name: 'Trade',
    icon: '💰',
    color: 'yellow',
    description: 'Generate income through commerce, sales, or business',
    duration: 7,
    requiresFacilityTypes: ['storehouse', 'trading_post', 'pub', 'workshop'],
    examples: ['Sell goods', 'Run tavern', 'Trade routes']
  }
};

// ============================================
// BASTION SIZE TIERS (Grid-Based Layout System)
// ============================================
// [PURPOSE]: Define bastion size progression and grid dimensions
// [WHY]: Enable spatial grid planning UI for facility placement
// [FEATURE]: Visual floor plan designer for stronghold layout

export const BASTION_SIZE_TIERS = {
  cramped: {
    id: 'cramped',
    gridSize: [5, 5],
    label: 'Cramped (5×5)',
    description: 'A small, basic stronghold with limited space',
    slots: 2,
    minLevel: 1,
    upgradeCost: 0, // Starting size
    icon: '🏚️'
  },
  roomy: {
    id: 'roomy',
    gridSize: [7, 7],
    label: 'Roomy (7×7)',
    description: 'A comfortable stronghold with adequate space',
    slots: 4,
    minLevel: 5,
    upgradeCost: 2000,
    icon: '🏘️'
  },
  vast: {
    id: 'vast',
    gridSize: [10, 10],
    label: 'Vast (10×10)',
    description: 'An expansive stronghold with plenty of room',
    slots: 5,
    minLevel: 9,
    upgradeCost: 5000,
    icon: '🏰'
  },
  grandiose: {
    id: 'grandiose',
    gridSize: [12, 12],
    label: 'Grandiose (12×12)',
    description: 'A magnificent fortress with vast grounds',
    slots: 6,
    minLevel: 13,
    upgradeCost: 10000,
    icon: '🏛️'
  },
  // [PB-006 FIX 2026-05-09]: Party Bastion tiers — bigger than personal because
  // every party member contributes their share of the space. Used by
  // PartyBastionPanel.getBastionSize which scales by facility count, NOT by level.
  // Personal bastion never reaches these tiers (it maps by level).
  modest: {
    id: 'modest',
    gridSize: [8, 8],
    label: 'Modest Party Stronghold (8×8)',
    description: 'A small shared stronghold the party has just begun building',
    slots: 4,
    minLevel: 1,
    upgradeCost: 0,
    icon: '🏘️'
  },
  estate: {
    id: 'estate',
    gridSize: [11, 11],
    label: 'Party Estate (11×11)',
    description: 'The party has carved out a real foothold together',
    slots: 6,
    minLevel: 1,
    upgradeCost: 0,
    icon: '🏯'
  },
  stronghold: {
    id: 'stronghold',
    gridSize: [13, 13],
    label: 'Party Stronghold (13×13)',
    description: 'A formidable shared base, large enough for everyone\'s ambitions',
    slots: 8,
    minLevel: 1,
    upgradeCost: 0,
    icon: '🏰'
  },
  fortress: {
    id: 'fortress',
    gridSize: [16, 16],
    label: 'Party Fortress (16×16)',
    description: 'A grand party fortress — the stuff of legends',
    slots: 12,
    minLevel: 1,
    upgradeCost: 0,
    icon: '🏛️'
  }
};

// ============================================
// BASIC FACILITIES (Always Available)
// ============================================

export const BASIC_FACILITIES = {
  cramped: {
    id: 'cramped',
    name: 'Cramped Space',
    size: 'cramped',
    gridSize: [1, 1], // Grid placement: 1×1 cells
    cost: 0,
    buildTime: 0,
    minLevel: 1,
    description: 'A small, basic room with minimal amenities. Can be used for storage or simple tasks.',
    orders: ['maintain'],
    capacity: 1, // How many hirelings can work here
    icon: '📦',
    category: 'Basic'
  },
  roomy: {
    id: 'roomy',
    name: 'Roomy Space',
    size: 'roomy',
    gridSize: [2, 2], // Grid placement: 2×2 cells
    cost: 0,
    buildTime: 0,
    minLevel: 1,
    description: 'A comfortable room with adequate space for various activities.',
    orders: ['maintain'],
    capacity: 2,
    icon: '🏠',
    category: 'Basic'
  }
};

// ============================================
// SPECIAL FACILITIES (By Level)
// ============================================

export const SPECIAL_FACILITIES = [
  // === LEVEL 5 FACILITIES ===
  {
    id: 'arcane_study',
    name: 'Arcane Study',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 5,
    cost: 1000,
    buildTime: 30,
    description: 'A chamber filled with mystical tomes and arcane implements for magical research.',
    orders: ['research', 'empower', 'maintain'],
    capacity: 2,
    icon: '🔮',
    benefits: [
      'Identify magic items (1 per turn)',
      'Research spell scrolls',
      'Study magical phenomena'
    ],
    category: 'Magical',
    // [NEW]: Facility upgrade system (Level 1 → Level 2 → Level 3)
    upgrades: {
      level1: {
        name: 'Basic Arcane Study',
        benefits: ['Identify 1 magic item per turn', 'Research common spell scrolls'],
        capacity: 2
      },
      level2: {
        name: 'Standard Arcane Study',
        cost: 2000,
        buildTime: 20,
        benefits: ['Identify 2 magic items per turn', 'Research uncommon spell scrolls', 'Detect magic at will'],
        capacity: 3
      },
      level3: {
        name: 'Advanced Arcane Study',
        cost: 4000,
        buildTime: 30,
        benefits: ['Identify 3 magic items per turn', 'Research rare spell scrolls', 'Permanent detect magic', '+1 to spell attack rolls when resting'],
        capacity: 4
      }
    }
  },
  {
    id: 'smithy',
    name: 'Smithy',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 5,
    cost: 750,
    buildTime: 20,
    description: 'A forge and workshop for crafting weapons, armor, and metal goods.',
    orders: ['craft', 'maintain'],
    capacity: 2,
    icon: '⚒️',
    benefits: [
      'Craft mundane weapons and armor',
      'Repair damaged equipment',
      'Smelt ore into ingots'
    ],
    category: 'Crafting',
    upgrades: {
      level1: {
        name: 'Basic Smithy',
        benefits: ['Craft mundane weapons and armor', 'Repair equipment'],
        capacity: 2
      },
      level2: {
        name: 'Master Smithy',
        cost: 1500,
        buildTime: 15,
        benefits: ['Craft +1 weapons and armor', 'Repair magical items', 'Faster crafting (-25% time)'],
        capacity: 3
      },
      level3: {
        name: 'Legendary Forge',
        cost: 3000,
        buildTime: 20,
        benefits: ['Craft +2 weapons and armor', 'Craft rare items', 'Enchant mundane items', '50% crafting cost reduction'],
        capacity: 4
      }
    }
  },
  {
    id: 'garden',
    name: 'Garden',
    gridSize: [1, 1], // Grid placement: 1×1 cells (small)
    minLevel: 5,
    cost: 500,
    buildTime: 15,
    description: 'A cultivated plot for growing herbs, vegetables, and useful plants.',
    orders: ['harvest', 'maintain'],
    capacity: 1,
    icon: '🌿',
    benefits: [
      'Generate food supplies',
      'Grow herbalism kit components',
      'Earn 10-30 gp per harvest'
    ],
    category: 'Resource'
  },
  {
    id: 'barracks',
    name: 'Barracks',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 5,
    cost: 800,
    buildTime: 25,
    description: 'Lodging for soldiers and defenders, with training facilities.',
    orders: ['recruit', 'maintain'],
    capacity: 10, // Can house 10 defenders
    icon: '🛡️',
    benefits: [
      'Recruit up to 10 defenders',
      'Train defenders (improve stats)',
      'Reduce upkeep costs'
    ],
    category: 'Defense'
  },
  {
    id: 'library',
    name: 'Library',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 5,
    cost: 1200,
    buildTime: 35,
    description: 'A collection of books, scrolls, and knowledge from across the realms.',
    orders: ['research', 'maintain'],
    capacity: 3,
    icon: '📚',
    benefits: [
      'Research lore and history',
      'Gain advantage on Intelligence checks',
      'Copy spells and scrolls'
    ],
    category: 'Knowledge'
  },
  {
    id: 'pub',
    name: 'Pub',
    gridSize: [2, 2], // Grid placement: 2×2 cells (medium)
    minLevel: 5,
    cost: 600,
    buildTime: 20,
    description: 'A tavern serving food and drink, attracting locals and travelers.',
    orders: ['trade', 'recruit', 'research', 'maintain'],
    capacity: 5,
    icon: '🍺',
    benefits: [
      'Earn 20-50 gp per trade order',
      'Gather rumors and information',
      'Recruit hirelings'
    ],
    category: 'Social'
  },
  {
    id: 'storehouse',
    name: 'Storehouse',
    gridSize: [2, 2], // Grid placement: 2×2 cells (medium)
    minLevel: 5,
    cost: 500,
    buildTime: 15,
    description: 'A warehouse for storing goods and facilitating trade.',
    orders: ['trade', 'maintain'],
    capacity: 2,
    icon: '📦',
    benefits: [
      'Earn 30-80 gp per trade order (increases with level)',
      'Store inventory safely',
      'Trade routes expansion'
    ],
    category: 'Commerce'
  },
  {
    id: 'workshop',
    name: 'Workshop',
    gridSize: [2, 2], // Grid placement: 2×2 cells (medium)
    minLevel: 5,
    cost: 700,
    buildTime: 20,
    description: 'A versatile crafting space for artisans and crafters.',
    orders: ['craft', 'trade', 'maintain'],
    capacity: 3,
    icon: '🔨',
    benefits: [
      'Craft tools and artisan goods',
      'Earn income from sales',
      'Support tool proficiencies'
    ],
    category: 'Crafting'
  },

  // === LEVEL 9 FACILITIES ===
  {
    id: 'laboratory',
    name: 'Laboratory',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 9,
    cost: 2500,
    buildTime: 40,
    description: 'An advanced alchemical workshop for creating potions of any rarity.',
    orders: ['craft', 'research', 'maintain'],
    capacity: 3,
    icon: '⚗️',
    benefits: [
      'Craft potions of ANY rarity',
      'Alchemical experiments',
      'Poison creation'
    ],
    category: 'Magical',
    recommendedPrereqs: ['garden', 'arcane_study'] // Herbs + magical knowledge
  },
  {
    id: 'greenhouse',
    name: 'Greenhouse',
    gridSize: [2, 2], // Grid placement: 2×2 cells (medium)
    minLevel: 9,
    cost: 1800,
    buildTime: 30,
    description: 'An enchanted greenhouse growing exotic and magical plants.',
    orders: ['harvest', 'maintain'],
    capacity: 2,
    icon: '🌺',
    benefits: [
      'Grow rare herbs and plants',
      'Spell components',
      'Earn 40-100 gp per harvest'
    ],
    category: 'Resource',
    recommendedPrereqs: ['garden'] // Upgrade from garden
  },
  {
    id: 'sanctum',
    name: 'Sanctum',
    gridSize: [2, 2], // Grid placement: 2×2 cells (medium)
    minLevel: 9,
    cost: 2000,
    buildTime: 35,
    description: 'A sacred or profane chamber resonating with divine or dark energy.',
    orders: ['empower', 'research', 'maintain'],
    capacity: 2,
    icon: '✨',
    benefits: [
      'Channel divine power',
      'Create holy/unholy items',
      'Commune with deities'
    ],
    category: 'Magical',
    recommendedPrereqs: ['library'] // Divine texts
  },
  {
    id: 'stable',
    name: 'Stable',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 9,
    cost: 1500,
    buildTime: 25,
    description: 'Housing for mounts, war animals, and exotic beasts.',
    orders: ['recruit', 'maintain'],
    capacity: 8,
    icon: '🐴',
    benefits: [
      'House up to 8 mounts',
      'Train war animals',
      'Breed exotic creatures'
    ],
    category: 'Defense'
    // No prereqs - standalone
  },
  {
    id: 'training_area',
    name: 'Training Area',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 9,
    cost: 1600,
    buildTime: 30,
    description: 'Obstacle courses, sparring rings, and equipment for martial training.',
    orders: ['empower', 'maintain'],
    capacity: 6,
    icon: '🎯',
    benefits: [
      'Train in weapons and armor',
      'Grant temporary combat bonuses',
      'Practice tactical maneuvers'
    ],
    category: 'Training',
    recommendedPrereqs: ['barracks', 'smithy'] // Soldiers + equipment
  },
  {
    id: 'trading_post',
    name: 'Trading Post',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 9,
    cost: 2200,
    buildTime: 35,
    description: 'A bustling market connecting to distant trade networks.',
    orders: ['trade', 'recruit', 'maintain'],
    capacity: 4,
    icon: '🏪',
    benefits: [
      'Earn 50-150 gp per trade',
      'Access to rare goods',
      'Expand trade networks'
    ],
    category: 'Commerce',
    recommendedPrereqs: ['storehouse'] // Goods to trade
  },

  // === LEVEL 13 FACILITIES ===
  {
    id: 'archive',
    name: 'Archive',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 13,
    cost: 3500,
    buildTime: 50,
    description: 'A vast collection of historical records and ancient knowledge.',
    orders: ['research', 'maintain'],
    capacity: 4,
    icon: '📜',
    benefits: [
      'Research obscure lore',
      'Advantage on History checks for 7 days',
      'Uncover lost secrets'
    ],
    category: 'Knowledge',
    recommendedPrereqs: ['library'] // Expand from library
  },
  {
    id: 'meditation_chamber',
    name: 'Meditation Chamber',
    gridSize: [1, 1], // Grid placement: 1×1 cells (small)
    minLevel: 13,
    cost: 3000,
    buildTime: 45,
    description: 'A serene space attuned to mental clarity and spiritual focus.',
    orders: ['empower', 'research', 'maintain'],
    capacity: 2,
    icon: '🧘',
    benefits: [
      'Reroll Bastion Events',
      'Gain temporary Inspiration',
      'Enhanced mental focus'
    ],
    category: 'Magical',
    recommendedPrereqs: ['sanctum'] // Spiritual connection
  },
  {
    id: 'observatory',
    name: 'Observatory',
    gridSize: [2, 2], // Grid placement: 2×2 cells (medium)
    minLevel: 13,
    cost: 3200,
    buildTime: 45,
    description: 'A tower with telescopes and star charts for celestial study.',
    orders: ['research', 'maintain'],
    capacity: 2,
    icon: '🔭',
    benefits: [
      'Predict weather and events',
      'Navigate by stars',
      'Research planar phenomena'
    ],
    category: 'Knowledge',
    recommendedPrereqs: ['arcane_study', 'library'] // Research foundation
  },
  {
    id: 'teleportation_circle',
    name: 'Teleportation Circle',
    gridSize: [1, 1], // Grid placement: 1×1 cells (small)
    minLevel: 13,
    cost: 5000,
    buildTime: 60,
    description: 'A permanent magical circle enabling instant teleportation.',
    orders: ['empower', 'maintain'],
    capacity: 1,
    icon: '🌀',
    benefits: [
      'Teleport to/from bastion (1/day)',
      'Connect to teleportation network',
      'Transport goods instantly'
    ],
    category: 'Magical',
    recommendedPrereqs: ['arcane_study'] // Arcane knowledge required
  },
  {
    id: 'trophy_room',
    name: 'Trophy Room',
    gridSize: [1, 1], // Grid placement: 1×1 cells (small)
    minLevel: 13,
    cost: 2800,
    buildTime: 40,
    description: 'A gallery displaying achievements, treasures, and trophies.',
    orders: ['empower', 'maintain'],
    capacity: 1,
    icon: '🏆',
    benefits: [
      'Display magic items (retain benefits)',
      'Inspire allies (morale bonus)',
      'Attract patrons and visitors'
    ],
    category: 'Social'
    // No prereqs - standalone showcase
  },
  {
    id: 'war_room',
    name: 'War Room',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 13,
    cost: 3300,
    buildTime: 45,
    description: 'A tactical command center with maps, intelligence, and strategy.',
    orders: ['empower', 'research', 'maintain'],
    capacity: 5,
    icon: '⚔️',
    benefits: [
      'Plan military operations',
      'Grant tactical advantages',
      'Coordinate allies'
    ],
    category: 'Defense',
    recommendedPrereqs: ['barracks', 'training_area'] // Military infrastructure
  },

  // === LEVEL 17 FACILITIES ===
  {
    id: 'demiplane',
    name: 'Demiplane',
    gridSize: [3, 3], // Grid placement: 3×3 cells (xlarge)
    minLevel: 17,
    cost: 10000,
    buildTime: 90,
    description: 'A pocket dimension accessible only to authorized individuals.',
    orders: ['empower', 'maintain'],
    capacity: 10,
    icon: '🌌',
    benefits: [
      'Extradimensional storage (unlimited)',
      'Safe haven (undetectable)',
      'Time flows differently (optional)'
    ],
    category: 'Magical',
    recommendedPrereqs: ['teleportation_circle', 'arcane_study'] // Planar magic
  },
  {
    id: 'guildhall',
    name: 'Guildhall',
    gridSize: [3, 3], // Grid placement: 3×3 cells (xlarge)
    minLevel: 17,
    cost: 8000,
    buildTime: 75,
    description: 'Headquarters for a guild, attracting artisans and specialists.',
    orders: ['recruit', 'trade', 'craft', 'maintain'],
    capacity: 15,
    icon: '🏛️',
    benefits: [
      'Recruit master craftsmen',
      'Earn 100-300 gp per trade',
      'Access guild resources'
    ],
    category: 'Social',
    recommendedPrereqs: ['trading_post', 'workshop'] // Commerce foundation
  },
  {
    id: 'menagerie',
    name: 'Menagerie',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 17,
    cost: 7500,
    buildTime: 70,
    description: 'A magical zoo housing rare and exotic creatures.',
    orders: ['recruit', 'harvest', 'maintain'],
    capacity: 12,
    icon: '🦁',
    benefits: [
      'Capture and house monsters',
      'Harvest exotic materials',
      'Train creature companions'
    ],
    category: 'Resource',
    recommendedPrereqs: ['stable', 'greenhouse'] // Animal/nature facilities
  },
  {
    id: 'sacral_sanctum',
    name: 'Sacral Sanctum',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 17,
    cost: 9000,
    buildTime: 80,
    description: 'A temple infused with overwhelming divine or infernal power.',
    orders: ['empower', 'research', 'maintain'],
    capacity: 6,
    icon: '⛪',
    benefits: [
      'Cast high-level divine spells',
      'Resurrect the dead',
      'Commune with outer planes'
    ],
    category: 'Magical',
    recommendedPrereqs: ['sanctum', 'meditation_chamber'] // Divine progression
  },
  {
    id: 'theater',
    name: 'Theater',
    gridSize: [3, 2], // Grid placement: 3×2 cells (large)
    minLevel: 17,
    cost: 6500,
    buildTime: 65,
    description: 'A grand performance hall attracting artists and audiences.',
    orders: ['trade', 'recruit', 'maintain'],
    capacity: 20,
    icon: '🎭',
    benefits: [
      'Earn 80-200 gp per trade',
      'Recruit bards and performers',
      'Boost morale and reputation'
    ],
    category: 'Social',
    recommendedPrereqs: ['pub'] // Social venue
  }
];

// ============================================
// BASTION EVENTS (Random Occurrences)
// ============================================

export const BASTION_EVENTS = [
  {
    id: 'peaceful_week',
    name: 'Peaceful Week',
    probability: 0.35,
    type: 'neutral',
    effect: 'Nothing unusual occurs. Your bastion operates smoothly.',
    impact: null,
    icon: '☮️'
  },
  {
    id: 'successful_trade',
    name: 'Successful Trade',
    probability: 0.12,
    type: 'positive',
    effect: 'Merchants pay premium prices for your goods.',
    impact: { gold: [50, 200] },
    icon: '💰'
  },
  {
    id: 'bountiful_harvest',
    name: 'Bountiful Harvest',
    probability: 0.10,
    type: 'positive',
    effect: 'Your gardens and farms yield exceptional crops.',
    impact: { resources: '+50%' },
    icon: '🌾'
  },
  {
    id: 'visitor_arrives',
    name: 'Visitor Arrives',
    probability: 0.08,
    type: 'quest',
    effect: 'A mysterious traveler seeks audience with you.',
    impact: { quest: true },
    icon: '🚶'
  },
  {
    id: 'skilled_artisan',
    name: 'Skilled Artisan Seeks Work',
    probability: 0.07,
    type: 'positive',
    effect: 'A talented craftsperson offers their services.',
    impact: { hireling: 'artisan' },
    icon: '👷'
  },
  {
    id: 'festival_day',
    name: 'Festival Day',
    probability: 0.05,
    type: 'positive',
    effect: 'Locals celebrate, boosting morale and trade.',
    impact: { gold: [30, 100], morale: '+1' },
    icon: '🎉'
  },
  {
    id: 'minor_damage',
    name: 'Minor Damage',
    probability: 0.10,
    type: 'negative',
    effect: 'A facility needs repair. Pay 50 gp or skip maintenance.',
    impact: { gold: -50, facilityDamaged: true },
    icon: '🔨'
  },
  {
    id: 'pest_infestation',
    name: 'Pest Infestation',
    probability: 0.05,
    type: 'negative',
    effect: 'Vermin invade a storage area. Lose some supplies.',
    impact: { supplies: '-20%' },
    icon: '🐀'
  },
  {
    id: 'bandit_scouts',
    name: 'Bandit Scouts Spotted',
    probability: 0.04,
    type: 'warning',
    effect: 'Bandits have been seen nearby. Consider hiring defenders.',
    impact: { threat: 'low' },
    icon: '⚠️'
  },
  {
    id: 'rare_book_found',
    name: 'Rare Book Found',
    probability: 0.03,
    type: 'positive',
    effect: 'A hireling discovers a valuable tome in the market.',
    impact: { knowledge: true },
    icon: '📕'
  },
  {
    id: 'weather_delay',
    name: 'Weather Delays Construction',
    probability: 0.03,
    type: 'negative',
    effect: 'Storms delay building projects by 3 days.',
    impact: { construction: +3 },
    icon: '🌧️'
  }
];

// ============================================
// BASTION TEMPLATES (DMG 2024 Presets)
// ============================================
// [PURPOSE]: Predefined bastion configurations for different playstyles
// [WHY]: Help players quickly set up their bastions based on character type

export const BASTION_TEMPLATES = {
  arcane_sanctuary: {
    name: 'Arcane Sanctuary',
    description: 'A magical stronghold focused on research, enchanting, and arcane power.',
    recommendedFor: ['Wizard', 'Sorcerer', 'Warlock'],
    icon: '🔮',
    facilities: [
      { type: 'arcane_study', priority: 1 },
      { type: 'library', priority: 2 },
      { type: 'laboratory', priority: 3 },
      { type: 'workshop', priority: 4 },        // [FIX]: scriptorium→workshop (scroll crafting)
      { type: 'observatory', priority: 5 },
      { type: 'sanctum', priority: 6 }          // [FIX]: enchanter_tower→sanctum
    ],
    defaultOrders: ['research', 'empower', 'craft']
  },

  martial_fortress: {
    name: 'Martial Fortress',
    description: 'A heavily fortified stronghold with strong defenses and military training.',
    recommendedFor: ['Fighter', 'Paladin', 'Ranger'],
    icon: '🏰',
    facilities: [
      { type: 'barracks', priority: 1 },
      { type: 'smithy', priority: 2 },
      { type: 'training_area', priority: 3 },   // [FIX]: armory→training_area
      { type: 'stable', priority: 4 },          // [FIX]: watchtower→stable (mounts)
      { type: 'war_room', priority: 5 },
      { type: 'trophy_room', priority: 6 }      // [FIX]: fortress_walls→trophy_room
    ],
    defaultOrders: ['recruit', 'craft', 'maintain']
  },

  holy_temple: {
    name: 'Holy Temple',
    description: 'A sacred bastion dedicated to divine worship and healing.',
    recommendedFor: ['Cleric', 'Paladin'],
    icon: '⛪',
    facilities: [
      { type: 'sanctum', priority: 1 },         // [FIX]: sanctuary→sanctum
      { type: 'garden', priority: 2 },
      { type: 'library', priority: 3 },
      { type: 'barracks', priority: 4 },
      { type: 'pub', priority: 5 },             // [FIX]: kitchen→pub (hospitality)
      { type: 'sacral_sanctum', priority: 6 }   // [FIX]: feast_hall→sacral_sanctum (divine)
    ],
    defaultOrders: ['empower', 'harvest', 'maintain']
  },

  thieves_den: {
    name: "Thieves' Den",
    description: 'A secretive hideout for espionage, trade, and covert operations.',
    recommendedFor: ['Rogue', 'Bard', 'Ranger'],
    icon: '🗝️',
    facilities: [
      { type: 'workshop', priority: 1 },
      { type: 'storehouse', priority: 2 },
      { type: 'trading_post', priority: 3 },    // [FIX]: marketplace→trading_post
      { type: 'storehouse', priority: 4 },      // [FIX]: bank→storehouse (vault storage)
      { type: 'pub', priority: 5 },             // [FIX]: common_room→pub
      { type: 'stable', priority: 6 }           // [FIX]: docks→stable (getaway mounts)
    ],
    defaultOrders: ['trade', 'craft', 'research']
  },

  natural_grove: {
    name: 'Natural Grove',
    description: 'A harmonious sanctuary integrated with nature.',
    recommendedFor: ['Druid', 'Ranger'],
    icon: '🌳',
    facilities: [
      { type: 'garden', priority: 1 },
      { type: 'sanctum', priority: 2 },         // [FIX]: sanctuary→sanctum
      { type: 'greenhouse', priority: 3 },      // [FIX]: laboratory→greenhouse (nature theme)
      { type: 'stable', priority: 4 },          // [FIX]: stables→stable (correct id)
      { type: 'menagerie', priority: 5 },       // [FIX]: kitchen→menagerie (creatures)
      { type: 'observatory', priority: 6 }
    ],
    defaultOrders: ['harvest', 'research', 'maintain']
  },

  merchant_hall: {
    name: 'Merchant Hall',
    description: 'A commercial hub focused on trade, craftsmanship, and wealth.',
    recommendedFor: ['Any class'],
    icon: '💰',
    facilities: [
      { type: 'trading_post', priority: 1 },    // [FIX]: marketplace→trading_post
      { type: 'storehouse', priority: 2 },
      { type: 'workshop', priority: 3 },
      { type: 'smithy', priority: 4 },
      { type: 'guildhall', priority: 5 },       // [FIX]: bank→guildhall (commerce hub)
      { type: 'theater', priority: 6 }          // [FIX]: feast_hall→theater (entertainment)
    ],
    defaultOrders: ['trade', 'craft', 'harvest']
  },

  balanced_keep: {
    name: 'Balanced Keep',
    description: 'A well-rounded bastion with diverse facilities.',
    recommendedFor: ['Any class'],
    icon: '⚖️',
    facilities: [
      { type: 'barracks', priority: 1 },
      { type: 'arcane_study', priority: 2 },
      { type: 'smithy', priority: 3 },
      { type: 'trading_post', priority: 4 },    // [FIX]: marketplace→trading_post
      { type: 'garden', priority: 5 },
      { type: 'library', priority: 6 }
    ],
    defaultOrders: ['craft', 'trade', 'research']
  }
};

// ============================================
// DEFENDER TYPES
// ============================================

export const DEFENDER_TYPES = {
  guard: {
    name: 'Guard',
    cost: 5, // gp per week
    stats: { ac: 14, hp: 11, attack: '+3 (1d6+1)' },
    description: 'Basic soldier trained in weaponry',
    icon: '🛡️'
  },
  archer: {
    name: 'Archer',
    cost: 7,
    stats: { ac: 13, hp: 9, attack: '+4 (1d8+2)' },
    description: 'Skilled ranged combatant',
    icon: '🏹'
  },
  scout: {
    name: 'Scout',
    cost: 8,
    stats: { ac: 15, hp: 16, attack: '+4 (1d6+2)' },
    description: 'Stealthy ranger with survival skills',
    icon: '👁️'
  },
  knight: {
    name: 'Knight',
    cost: 15,
    stats: { ac: 18, hp: 52, attack: '+5 (1d8+3)' },
    description: 'Elite warrior in plate armor',
    icon: '⚔️'
  },
  mage: {
    name: 'Mage',
    cost: 20,
    stats: { ac: 12, hp: 22, spells: '3rd level' },
    description: 'Spellcaster trained in arcane magic',
    icon: '🔮'
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get available facilities for a given character level
 */
export const getAvailableFacilities = (level) => {
  return SPECIAL_FACILITIES.filter(f => f.minLevel <= level);
};

/**
 * Get maximum facility slots for a given level
 */
export const getMaxFacilitySlots = (level) => {
  if (level < 5) return 0;
  if (level < 9) return 2; // 2 special + 2 basic = 4 total
  if (level < 13) return 4; // 4 special + 2 basic = 6 total
  if (level < 17) return 5; // 5 special + 2 basic = 7 total
  return 6; // 6 special + 2 basic = 8 total
};

/**
 * Check if a facility is available for construction
 */
export const canBuildFacility = (facility, playerLevel, currentFacilities) => {
  // Check level requirement
  if (facility.minLevel > playerLevel) return false;

  // Check if already built
  if (currentFacilities.some(f => f.type === facility.id)) return false;

  // Check facility slots
  const maxSlots = getMaxFacilitySlots(playerLevel);
  const specialFacilities = currentFacilities.filter(f => f.type !== 'cramped' && f.type !== 'roomy');
  if (specialFacilities.length >= maxSlots) return false;

  return true;
};

/**
 * Calculate days until next bastion turn
 */
export const getDaysUntilBastionTurn = (currentDay, lastBastionTurn) => {
  const daysSinceLastTurn = currentDay - lastBastionTurn;
  return Math.max(0, 7 - daysSinceLastTurn);
};

/**
 * Get facility by ID
 */
export const getFacilityData = (facilityId) => {
  // Check basic facilities
  if (BASIC_FACILITIES[facilityId]) {
    return BASIC_FACILITIES[facilityId];
  }

  // Check special facilities
  return SPECIAL_FACILITIES.find(f => f.id === facilityId);
};

/**
 * Get random bastion event
 */
export const rollBastionEvent = () => {
  const roll = Math.random();
  let cumulative = 0;

  for (const event of BASTION_EVENTS) {
    cumulative += event.probability;
    if (roll <= cumulative) {
      return event;
    }
  }

  // Fallback to peaceful week
  return BASTION_EVENTS[0];
};

/**
 * Calculate order completion
 */
export const isOrderComplete = (order, currentDay) => {
  return currentDay >= order.completionDay;
};

/**
 * Get orders available for a facility
 */
export const getAvailableOrders = (facilityType) => {
  const facility = getFacilityData(facilityType);
  if (!facility) return [];

  return facility.orders.map(orderKey => BASTION_ORDERS[orderKey]).filter(Boolean);
};

/**
 * Process bastion turn (called every 7 days)
 * [PURPOSE]: Handle all bastion turn mechanics per D&D 2024 DMG rules
 * [WHY]: Automatically process facility construction, complete orders, generate events
 * @param {Object} bastion - Current bastion state
 * @param {number} currentGameDay - Current game day
 * @returns {Object} - Updated bastion state with results
 */
export const processBastionTurn = (bastion, currentGameDay, settings = null) => {
  if (!bastion || !bastion.unlocked) return null;
  // [FIX MAY 2026]: `established` gate prevents auto-firing for users who
  //   were given a bastion stub at the level threshold but never opted into
  //   active bastion operations. BastionPanel surfaces a "Begin Bastion
  //   Operations" CTA that flips this flag. Backward-compat: old saves
  //   without `established` are treated as established (`=== false` only
  //   blocks explicit opt-out).
  if (bastion.established === false) return null;

  // [BSR-007 FIX — MAY 2026]: enableBastionUpgrades setting is consulted
  //   when handling upgrade COMPLETIONS below. Pre-fix, even if the user
  //   turned the toggle OFF after starting an upgrade, processBastionTurn
  //   still completed it on the next turn and bumped facility.upgradeLevel.
  //   When user re-enabled later, they'd see a level they don't remember
  //   earning. Now: if enableBastionUpgrades === false, upgrade completion
  //   is paused (status stays 'upgrading'); resumes when re-enabled.
  //   Backward-compat: settings null OR setting omitted → behaves as if
  //   enabled (matches pre-fix behavior for callers not yet threading
  //   settings through).
  const upgradesEnabled = !settings || settings.enableBastionUpgrades !== false;

  // Check if it's time for a bastion turn (every 7 days)
  const daysSinceLastTurn = currentGameDay - (bastion.lastBastionTurn || 0);
  if (daysSinceLastTurn < 7) return null;

  const results = {
    facilitiesCompleted: [],
    ordersCompleted: [],
    event: null,
    goldEarned: 0,
    goldSpent: 0,
    messages: []
  };

  // 1. Complete facility construction and upgrades
  const updatedFacilities = bastion.facilities.map(facility => {
    // Handle building completion
    if (facility.status === 'building' && currentGameDay >= facility.completionDay) {
      const facilityData = getFacilityData(facility.type);
      const facilityName = facilityData?.name || facility.type;

      results.facilitiesCompleted.push({
        name: facilityName,
        id: facility.id
      });
      results.messages.push(`✅ ${facilityName} construction complete!`);

      // [PHASE 6A]: Emit BASTION_FACILITY_BUILT event
      systemBus.emit(EVENTS.BASTION_FACILITY_BUILT, {
        facility: {
          id: facility.id,
          type: facility.type,
          name: facilityName,
          upgradeLevel: 1
        },
        bastionId: bastion.id,
        gameDay: currentGameDay,
        buildTime: currentGameDay - (facility.startDay || currentGameDay)
      });

      return { ...facility, status: 'active', upgradeLevel: 1 };
    }

    // Handle upgrade completion
    // [BSR-007 FIX — MAY 2026]: Skip completion when upgrades disabled.
    if (facility.status === 'upgrading' && currentGameDay >= facility.upgradeCompletionDay && upgradesEnabled) {
      const facilityName = getFacilityData(facility.type)?.name || facility.type;
      const newLevel = facility.nextLevel;

      results.facilitiesCompleted.push({
        name: `${facilityName} (Upgrade to Level ${newLevel})`,
        id: facility.id
      });
      results.messages.push(`⬆️ ${facilityName} upgraded to Level ${newLevel}!`);

      // [PHASE 6A]: Emit BASTION_FACILITY_UPGRADED event
      systemBus.emit(EVENTS.BASTION_FACILITY_UPGRADED, {
        facility: {
          id: facility.id,
          type: facility.type,
          name: facilityName,
          oldLevel: facility.upgradeLevel,
          newLevel: newLevel
        },
        bastionId: bastion.id,
        gameDay: currentGameDay,
        upgradeTime: currentGameDay - (facility.upgradeStartDay || currentGameDay)
      });

      return {
        ...facility,
        status: 'active',
        upgradeLevel: newLevel,
        nextLevel: null,
        upgradeStartDay: null,
        upgradeCompletionDay: null
      };
    }

    return facility;
  });

  // 2. Complete orders
  const completedOrders = [];
  const remainingOrders = [];

  bastion.activeOrders.forEach(order => {
    if (currentGameDay >= order.completionDay) {
      const orderData = BASTION_ORDERS[order.order];

      // Find the facility that issued this order to get its upgrade level and type
      const facility = bastion.facilities.find(f => f.id === order.facilityId);
      const facilityLevel = facility?.upgradeLevel || 1;
      const facilityType = facility?.type || null;

      // [PHASE 3]: Calculate rewards WITH facility type for item creation
      // [PHASE 1.1]: Pass order parameters to use player-selected items
      const reward = calculateOrderRewards(order.order, facilityLevel, facilityType, order.parameters);

      // Process reward and create message
      let messageIcon = '';
      let messageText = '';

      // [PHASE 3]: Handle item creation rewards
      if (reward.createItem && reward.itemData) {
        messageIcon = '🎁';
        messageText = `${orderData?.name} complete! Created: ${reward.itemData.displayName}`;
        // Item will be added to inventory by App.jsx
      }
      // Gold rewards
      else if (reward.gold) {
        results.goldEarned += reward.gold;
        messageIcon = '💰';
        messageText = `${orderData?.name} complete! Earned ${reward.gold} gp`;
      }
      // Legacy text rewards (for backwards compatibility)
      else if (reward.item) {
        messageIcon = '🔨';
        messageText = `${orderData?.name} complete! Created ${reward.item}`;
      } else if (reward.scrolls) {
        messageIcon = '📜';
        messageText = `${orderData?.name} complete! Created ${reward.scrolls}`;
      } else if (reward.defenders) {
        messageIcon = '🛡️';
        messageText = `${orderData?.name} complete! Recruited ${reward.defenders}x ${reward.type}`;
      } else if (reward.items) {
        messageIcon = '🌾';
        messageText = `${orderData?.name} complete! Harvested ${reward.items}`;
      } else if (reward.effect) {
        messageIcon = '✨';
        messageText = `${orderData?.name} complete! ${reward.effect}`;
      } else {
        messageIcon = '✅';
        messageText = `${orderData?.name} complete!`;
      }

      // Add gold from harvest orders (can have both items AND gold)
      if (reward.gold && reward.createItem) {
        results.goldEarned += reward.gold;
        messageText += ` (+${reward.gold} gp)`;
      }

      results.messages.push(`${messageIcon} ${messageText}`);

      // [PHASE 6A]: Emit BASTION_ORDER_COMPLETED event for each completed order
      systemBus.emit(EVENTS.BASTION_ORDER_COMPLETED, {
        order: {
          id: order.id,
          type: order.order,
          facilityId: order.facilityId,
          facilityType: facilityType,
          parameters: order.parameters,
          startDay: order.startDay,
          completedDay: currentGameDay
        },
        reward: reward,
        bastionId: bastion.id,
        gameDay: currentGameDay
      });

      completedOrders.push({
        ...order,
        result: reward,
        completedDay: currentGameDay
      });
    } else {
      remainingOrders.push(order);
    }
  });

  results.ordersCompleted = completedOrders;

  // 3. Roll for random event
  const event = rollBastionEvent();
  results.event = event;

  // Apply event effects
  if (event.impact) {
    if (event.impact.gold) {
      results.goldEarned += event.impact.gold;
      results.messages.push(`${event.icon} ${event.name}: ${event.effect}`);
    }
  } else {
    results.messages.push(`${event.icon} ${event.name}`);
  }

  // 4. Calculate turn number
  // [PHASE 3.1 FIX]: Calculate turn number from bastion creation day, not last turn
  // [WHY]: Previous calculation always showed turn #1 (calculated from last turn)
  // [SOLUTION]: Use createdDay if available, otherwise count from history length
  const turnNumber = bastion.createdDay
    ? Math.floor((currentGameDay - bastion.createdDay) / 7) + 1
    : (bastion.history?.length || 0) + 1;

  // 5. Create history entry
  const historyEntry = {
    turnNumber,
    gameDay: currentGameDay,
    event: event.name,
    ordersCompleted: completedOrders.length,
    buildingsCompleted: results.facilitiesCompleted.length,
    goldEarned: results.goldEarned,
    goldSpent: results.goldSpent,
    notes: results.messages.join(' | ')
  };

  // [PHASE 6A]: Emit BASTION_TURN_COMPLETED event with comprehensive results
  systemBus.emit(EVENTS.BASTION_TURN_COMPLETED, {
    bastionId: bastion.id,
    turnNumber,
    gameDay: currentGameDay,
    event: event,
    facilitiesCompleted: results.facilitiesCompleted,
    ordersCompleted: completedOrders,
    goldEarned: results.goldEarned,
    goldSpent: results.goldSpent,
    treasury: (bastion.treasury || 0) + results.goldEarned - results.goldSpent,
    messages: results.messages
  });

  // 6. Return updated bastion state
  return {
    updatedBastion: {
      ...bastion,
      lastBastionTurn: currentGameDay,
      nextBastionTurn: currentGameDay + 7,
      facilities: updatedFacilities,
      activeOrders: remainingOrders,
      treasury: (bastion.treasury || 0) + results.goldEarned - results.goldSpent,
      history: [historyEntry, ...(bastion.history || [])]
    },
    results
  };
};

/**
 * Get upgrade information for a facility
 * [PURPOSE]: Retrieve upgrade details for current facility level
 * @param {Object} facility - Facility data
 * @param {number} currentLevel - Current upgrade level (1-3)
 * @returns {Object} - Upgrade information
 */
export const getFacilityUpgradeInfo = (facility, currentLevel = 1) => {
  const facilityData = getFacilityData(facility.type);
  if (!facilityData || !facilityData.upgrades) return null;

  const nextLevel = Math.min(currentLevel + 1, 3);
  const upgrade = facilityData.upgrades[`level${nextLevel}`];

  if (!upgrade) return null;

  return {
    currentLevel,
    nextLevel,
    canUpgrade: nextLevel <= 3,
    upgradeCost: upgrade.cost || 0,
    upgradeBuildTime: upgrade.buildTime || 0,
    currentBenefits: facilityData.upgrades[`level${currentLevel}`]?.benefits || [],
    nextBenefits: upgrade.benefits || [],
    capacityIncrease: upgrade.capacity - (facilityData.upgrades[`level${currentLevel}`]?.capacity || 0)
  };
};

/**
 * Check if facility can be upgraded
 * @param {Object} facility - Facility instance
 * @param {number} playerGold - Available gold
 * @returns {boolean}
 */
export const canUpgradeFacility = (facility, playerGold) => {
  const currentLevel = facility.upgradeLevel || 1;
  if (currentLevel >= 3) return false; // Max level

  const upgradeInfo = getFacilityUpgradeInfo(facility, currentLevel);
  if (!upgradeInfo) return false;

  return playerGold >= upgradeInfo.upgradeCost;
};

// ============================================
// [PHASE 3]: BASTION ITEM CREATION TABLES
// ============================================
// [PURPOSE]: Define what items each facility can create at each level
// [WHY]: D&D rules compliance - orders should create REAL items

/**
 * Smithy craft tables - weapons and armor by level.
 *
 * [APR 30 2026]: Migrated from hardcoded *_plus_N IDs (longsword_plus_1,
 * scale_mail_plus_1, shield_plus_2, etc.) — those BASE_ITEMS rows were
 * deleted when the template resolver layer landed. Levels 2+3 now use
 * template references which the bastion item generator resolves
 * dynamically (random base + magicalBonus). Level 1 stays as raw
 * BASE_ITEMS IDs (non-magical crafting).
 */
const SMITHY_CRAFT_TABLES = {
  level1: {
    weapons: ['longsword', 'shortsword', 'battleaxe', 'warhammer', 'mace', 'spear', 'handaxe', 'dagger'],
    armor: ['leather', 'studded_leather', 'chain_shirt', 'scale_mail', 'shield']
  },
  level2: {
    // +1 magical crafting — template-resolved at order-completion time.
    weapons: [{ template: 'weapon_plus_n', n: 1 }],
    armor: [{ template: 'armor_plus_n', n: 1 }, { template: 'shield_plus_n', n: 1 }]
  },
  level3: {
    // Mix of +2 magical and high-tier mundane (greatsword/maul are level-3
    // tier mundane weapons that don't need magical bonuses to be valuable).
    weapons: [{ template: 'weapon_plus_n', n: 2 }, 'greatsword', 'greataxe', 'maul'],
    armor: [{ template: 'armor_plus_n', n: 1 }, { template: 'shield_plus_n', n: 2 }]
  }
};

/**
 * Workshop craft tables - tools and gear
 */
const WORKSHOP_CRAFT_TABLES = {
  level1: ['thieves_tools', 'smiths_tools', 'carpenters_tools', 'rope_hempen_50', 'lantern_hooded', 'backpack'],
  level2: ['climbers_kit', 'disguise_kit', 'healers_kit', 'rope_silk_50', 'grappling_hook'],
  level3: ['thieves_tools_masterwork', 'smiths_tools_masterwork', 'spyglass', 'magnifying_glass']
};

/**
 * Laboratory craft tables - potions
 */
const LABORATORY_CRAFT_TABLES = {
  level1: ['potion_healing', 'antitoxin', 'acid_vial', 'alchemists_fire'],
  level2: ['potion_greater_healing', 'potion_climbing', 'potion_water_breathing'],
  level3: ['potion_superior_healing', 'potion_heroism', 'potion_invisibility']
};

/**
 * Harvest resource tables
 * [PHASE 3.4]: Fixed to use actual items from gameData.js
 * [WHY]: Previous items didn't exist, causing harvest orders to fail
 *
 * Gardens produce: food (rations), herbs, healing kits, basic components
 * Greenhouses produce: magical herbs, spell components, valuable materials
 */
const HARVEST_RESOURCE_TABLES = {
  // Garden harvests - food and basic healing supplies
  garden_level1: ['rations', 'herbs'],  // Basic food and herbs
  garden_level2: ['rations', 'herbs', 'healers_kit'],  // Quality supplies
  garden_level3: ['rations', 'herbs', 'pearl_100gp', 'ruby_dust_50gp'],  // Food + valuable components

  // Greenhouse harvests - magical components and rare materials
  greenhouse_level1: ['herbs', 'incense', 'bat_guano', 'sulfur'],  // Common spell components
  greenhouse_level2: ['pearl_100gp', 'jade_dust_10gp', 'gold_dust_25gp'],  // Valuable components
  greenhouse_level3: ['diamond_300gp', 'diamond_500gp', 'ruby_dust_50gp']  // Rare magical materials
};

/**
 * Generate a random inventory item from bastion order
 * [PURPOSE]: Create actual item instances that can be added to inventory
 * [WHY]: D&D compliance - smithy should create real longswords, not text
 *
 * @param {string} facilityType - Type of facility (smithy, laboratory, workshop, etc.)
 * @param {number} facilityLevel - Upgrade level (1-3)
 * @param {string} orderType - Type of order (craft, harvest, research)
 * @returns {Object|null} - Item object ready to add to inventory, or null
 */
export const generateBastionItem = (facilityType, facilityLevel = 1, orderType = 'craft') => {
  let itemTable = [];
  let itemCategory = '';

  // [CRAFT ORDERS]: Generate items based on facility type
  if (orderType === 'craft') {
    if (facilityType === 'smithy') {
      const levelKey = `level${facilityLevel}`;
      const tables = SMITHY_CRAFT_TABLES[levelKey];

      // Randomly choose weapon or armor
      if (Math.random() < 0.6) {
        // 60% chance weapon
        itemTable = tables.weapons;
        itemCategory = 'weapons';
      } else {
        // 40% chance armor
        itemTable = tables.armor;
        itemCategory = 'armor';
      }
    } else if (facilityType === 'workshop') {
      itemTable = WORKSHOP_CRAFT_TABLES[`level${facilityLevel}`];
      itemCategory = 'items';
    } else if (facilityType === 'laboratory') {
      itemTable = LABORATORY_CRAFT_TABLES[`level${facilityLevel}`];
      itemCategory = 'items';
    }
  }

  // [HARVEST ORDERS]: Generate resource items
  else if (orderType === 'harvest') {
    if (facilityType === 'garden') {
      itemTable = HARVEST_RESOURCE_TABLES[`garden_level${facilityLevel}`];
      itemCategory = 'items';
    } else if (facilityType === 'greenhouse') {
      itemTable = HARVEST_RESOURCE_TABLES[`greenhouse_level${facilityLevel}`];
      itemCategory = 'items';
    }
  }

  // Select random item from table
  if (itemTable.length === 0) return null;

  const selected = itemTable[Math.floor(Math.random() * itemTable.length)];

  // [APR 30 2026]: Smithy tables for levels 2+3 may contain template
  // entries (e.g., { template: 'weapon_plus_n', n: 1 }) instead of raw
  // BASE_ITEMS IDs. Resolve via the shared template resolver layer; the
  // resolver picks a random base item and we forward its id + magical
  // properties so the inventory consumer instantiates it correctly.
  if (selected && typeof selected === 'object' && selected.template) {
    // Lazy import to avoid circular: bastionData → templateResolvers → BASE_ITEMS.
    // eslint-disable-next-line global-require
    const { resolveTemplateEntry } = require('../services/templateResolvers');
    const resolved = resolveTemplateEntry(selected);
    if (!resolved) return null;
    const displayName = resolved.id.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    return {
      itemKey: resolved.id,
      category: itemCategory,
      displayName: `+${resolved.properties.magicalBonus} ${displayName}`,
      properties: resolved.properties,
    };
  }

  const selectedItemKey = selected;
  return {
    itemKey: selectedItemKey,
    category: itemCategory,
    displayName: selectedItemKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  };
};

/**
 * Calculate order rewards based on facility level and order type
 * [PURPOSE]: Generate actual rewards from completed orders
 * [PHASE 3 UPGRADE]: Now creates real items instead of text descriptions
 * [PHASE 1.1 UPGRADE]: Now uses player-selected items from order parameters
 *
 * @param {string} orderType - Type of order
 * @param {number} facilityLevel - Upgrade level of facility (1-3)
 * @param {string} facilityType - Type of facility (smithy, laboratory, etc.)
 * @param {Object} parameters - Order parameters (may contain selectedItem)
 * @returns {Object} - Reward details with createItem flag
 */
export const calculateOrderRewards = (orderType, facilityLevel = 1, facilityType = null, parameters = {}) => {
  // [PHASE 1.1]: Check if player selected a specific item (from CraftOrderDialog)
  // [FIX NOV 26, 2025]: Also check selectedSpell for research orders
  const getItemData = (facilityType, facilityLevel, orderType) => {
    // If player selected an item via CraftOrderDialog, use it
    if (parameters.selectedItem) {
      return parameters.selectedItem;
    }
    // [FIX]: If player selected a spell via ResearchSpellDialog, use it
    // [WHY]: Research orders were creating generic "Spell Scroll (1st Level)" instead of
    //        the player's chosen spell like "Spell Scroll (Bless)"
    if (parameters.selectedSpell) {
      return parameters.selectedSpell;
    }
    // Otherwise, generate random item (legacy behavior)
    return facilityType ? generateBastionItem(facilityType, facilityLevel, orderType) : null;
  };

  const baseRewards = {
    craft: {
      level1: {
        createItem: true,
        itemData: getItemData(facilityType, 1, 'craft'),
        goldValue: 50,
        description: 'Crafted mundane equipment'
      },
      level2: {
        createItem: true,
        itemData: getItemData(facilityType, 2, 'craft'),
        goldValue: 150,
        description: 'Crafted +1 magical equipment'
      },
      level3: {
        createItem: true,
        itemData: getItemData(facilityType, 3, 'craft'),
        goldValue: 500,
        description: 'Crafted +2 rare equipment'
      }
    },
    trade: {
      level1: { gold: 50 + Math.floor(Math.random() * 50) }, // 50-100 gp
      level2: { gold: 100 + Math.floor(Math.random() * 100) }, // 100-200 gp
      level3: { gold: 200 + Math.floor(Math.random() * 200) } // 200-400 gp
    },
    harvest: {
      level1: {
        gold: 20 + Math.floor(Math.random() * 30),
        createItem: true,
        itemData: facilityType ? generateBastionItem(facilityType, 1, 'harvest') : null,
        description: 'Harvested rations, herbs, or spell components'
      },
      level2: {
        gold: 40 + Math.floor(Math.random() * 60),
        createItem: true,
        itemData: facilityType ? generateBastionItem(facilityType, 2, 'harvest') : null,
        description: 'Harvested quality supplies and valuable components'
      },
      level3: {
        gold: 80 + Math.floor(Math.random() * 120),
        createItem: true,
        itemData: facilityType ? generateBastionItem(facilityType, 3, 'harvest') : null,
        description: 'Harvested rare spell components and precious materials'
      }
    },
    research: {
      level1: {
        knowledge: 'Common lore',
        createItem: true,
        itemData: { itemKey: 'spell_scroll_1st_level', category: 'consumables', displayName: 'Spell Scroll (1st Level)' },
        description: 'Created 1st level spell scroll'
      },
      level2: {
        knowledge: 'Uncommon lore',
        createItem: true,
        itemData: { itemKey: 'spell_scroll_2nd_level', category: 'consumables', displayName: 'Spell Scroll (2nd Level)' },
        description: 'Created 2nd level spell scroll'
      },
      level3: {
        knowledge: 'Rare lore',
        createItem: true,
        itemData: { itemKey: 'spell_scroll_3rd_level', category: 'consumables', displayName: 'Spell Scroll (3rd Level)' },
        description: 'Created 3rd level spell scroll'
      }
    },
    empower: {
      level1: { effect: 'Minor magical enhancement' },
      level2: { effect: 'Moderate magical enhancement' },
      level3: { effect: 'Major magical enhancement' }
    },
    recruit: {
      level1: { defenders: 1, type: 'Guard' },
      level2: { defenders: 2, type: 'Veteran' },
      level3: { defenders: 3, type: 'Elite' }
    },
    maintain: {
      level1: { effect: 'Basic maintenance' },
      level2: { effect: 'Improved maintenance' },
      level3: { effect: 'Perfect maintenance' }
    }
  };

  const levelKey = `level${facilityLevel}`;
  let reward = baseRewards[orderType]?.[levelKey];

  // [FIX NOV 26, 2025]: Differentiate Library vs Arcane Study research
  // [WHY]: Both had 'research' order producing spell scrolls - confusing user
  // Library = knowledge/lore tokens, Arcane Study = spell scrolls (default)
  if (orderType === 'research' && facilityType === 'library') {
    // Library produces Lore Tokens instead of spell scrolls
    const loreTypes = {
      level1: { topic: 'History', skill: 'History' },
      level2: { topic: 'Arcana', skill: 'Arcana' },
      level3: { topic: 'Ancient Secrets', skill: 'Investigation' }
    };
    const lore = loreTypes[levelKey] || loreTypes.level1;

    reward = {
      ...reward,
      createItem: true,
      itemData: {
        itemKey: 'lore_token',
        category: 'consumables',
        displayName: `Lore Token (${lore.topic})`,
        description: `Grants advantage on one ${lore.skill} check. Single use.`,
        effect: { type: 'advantage', skill: lore.skill, uses: 1 }
      },
      description: `Researched ${lore.topic.toLowerCase()} lore`
    };
  }

  return reward || { gold: 0 };
};

export default {
  BASTION_ORDERS,
  BASTION_SIZE_TIERS,
  BASIC_FACILITIES,
  SPECIAL_FACILITIES,
  BASTION_EVENTS,
  BASTION_TEMPLATES,
  DEFENDER_TYPES,
  getAvailableFacilities,
  getMaxFacilitySlots,
  canBuildFacility,
  getDaysUntilBastionTurn,
  getFacilityData,
  rollBastionEvent,
  isOrderComplete,
  getAvailableOrders,
  processBastionTurn,
  getFacilityUpgradeInfo,
  canUpgradeFacility,
  calculateOrderRewards,
  generateBastionItem
};
